// Pages/Payments/SubscriptionPayment.jsx - Fixed back button for logged-in users
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import PaymentForm from '../../Pages/Billing/PaymentForm';
import { getBuildingSubscription } from '../../Fetch/fetchSubscription';
import styles from './SubscriptionPayment.module.css';

const SubscriptionPayment = () => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userProfile, setUserProfile] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Get data passed from login
    const { buildingId, reason, returnTo, userProfile: passedUserProfile } = location.state || {};

    useEffect(() => {
        // Check if user is logged in
        const checkLoginStatus = () => {
            const token = localStorage.getItem('token');
            const user = localStorage.getItem('user');
            const loggedIn = !!(token && user);
            setIsLoggedIn(loggedIn);
            return loggedIn;
        };

        // Try to get user profile from various sources
        const getUserProfile = () => {
            // First, try from location state (passed from login)
            if (passedUserProfile) {
                return passedUserProfile;
            }

            // Second, try from localStorage
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    return parsedUser;
                } catch (error) {
                }
            }

            // Third, try temp user (for non-logged in users)
            const tempUser = localStorage.getItem('tempUser');
            if (tempUser) {
                try {
                    const parsedTempUser = JSON.parse(tempUser);
                    return parsedTempUser;
                } catch (error) {
                }
            }

            return null;
        };

        checkLoginStatus();
        setUserProfile(getUserProfile());

        const loadSubscriptionData = async () => {
            if (!buildingId) {
                setError('מזהה בניין לא נמצא');
                setLoading(false);
                return;
            }

            try {
                const apiResponse = await getBuildingSubscription(buildingId);
                
                // Handle wrapped response structure (same as Login.jsx)
                let subscriptionData;
                if (apiResponse.success && apiResponse.data) {
                    subscriptionData = apiResponse.data;
                } else if (apiResponse.trialEndDate) {
                    subscriptionData = apiResponse;
                } else {
                    throw new Error('Invalid subscription response structure');
                }
                
                setSubscription(subscriptionData);
            } catch (err) {
                setError('שגיאה בטעינת נתוני המנוי');
            } finally {
                setLoading(false);
            }
        };

        loadSubscriptionData();
    }, [buildingId, passedUserProfile]);

    const handlePaymentSuccess = (paymentResponse) => {
        
        // Clear any temp user data
        localStorage.removeItem('tempUser');
        
        // Clear building subscriptions cache so it refreshes
        localStorage.removeItem('buildingSubscriptions');
        
        // Navigate to the return page (usually dashboard)
        const targetPage = returnTo || '/app/dashboard';
        navigate(targetPage, {
            state: {
                message: 'התשלום עובד בהצלחה! המנוי שלך פעיל עכשיו.',
                type: 'success'
            },
            replace: true
        });
    };

    const handleCancel = () => {
        
        // Clear any temp user data
        localStorage.removeItem('tempUser');
        
        if (isLoggedIn) {
            // User is logged in - return to dashboard or returnTo path
            const targetPage = returnTo || '/app/dashboard';
            navigate(targetPage, {
                state: {
                    message: 'התשלום בוטל. ניתן לחזור לתשלום בכל עת.',
                    type: 'info'
                },
                replace: true
            });
        } else {
            // User is not logged in - return to home/login
            navigate('/', {
                state: {
                    message: 'התשלום בוטל. תקופת הניסיון עדיין פגה.',
                    type: 'warning'
                },
                replace: true
            });
        }
    };

    // Get appropriate back button text based on login status
    const getBackButtonText = () => {
        if (isLoggedIn) {
            return 'חזרה לדשבורד';
        } else {
            return 'חזרה';
        }
    };

    // Get appropriate reason banner based on context
    const getReasonBanner = () => {
        if (reason === 'trial_expired') {
            return (
                <div className={styles.reasonBanner}>
                    <h3>תקופת הניסיון הסתיימה</h3>
                    <p>כדי להמשיך להשתמש במערכת ניהול ועד הבית, יש לבחור תוכנית מנוי.</p>
                </div>
            );
        } else if (reason === 'building_access' || reason === 'building_expired') {
            return (
                <div className={styles.reasonBanner}>
                    <h3>נדרש תשלום עבור בניין זה</h3>
                    <p>כדי לגשת לבניין זה, יש לבחור תוכנית מנוי.</p>
                </div>
            );
        } else if (reason === 'expiring_soon') {
            return (
                <div className={styles.reasonBanner}>
                    <h3>המנוי מסתיים בקרוב</h3>
                    <p>האריכו את המנוי שלכם כדי להמשיך ליהנות מהשירות.</p>
                </div>
            );
        } else if (reason === 'manual_payment') {
            return (
                <div className={styles.reasonBanner}>
                    <h3>תשלום מנוי</h3>
                    <p>בחרו תוכנית מנוי המתאימה לכם.</p>
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className={styles.subscriptionPaymentPage} dir="rtl">
                <div className={styles.loadingContainer}>
                    <Loader2 className={styles.spinner} />
                    <p>טוען נתוני מנוי...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.subscriptionPaymentPage} dir="rtl">
                <div className={styles.errorContainer}>
                    <AlertCircle className={styles.errorIcon} />
                    <h2>שגיאה</h2>
                    <p>{error}</p>
                    <button onClick={handleCancel} className={styles.backButton}>
                        <ArrowLeft size={16} />
                        {getBackButtonText()}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.subscriptionPaymentPage} dir="rtl">
            <div className={styles.paymentContainer}>
                {/* Header */}
                <div className={styles.header}>
                    <button onClick={handleCancel} className={styles.backButton}>
                        <ArrowLeft size={20} />
                        {getBackButtonText()}
                    </button>
                    <h1 className={styles.title}>
                        <CreditCard className={styles.titleIcon} />
                        תשלום מנוי אפליקציה
                    </h1>
                </div>

                {/* Dynamic Reason Banner */}
                {getReasonBanner()}

                {/* App Subscription Payment Form */}
                {subscription && (
                    <div className={styles.paymentFormWrapper}>
                        <PaymentForm
                            subscription={subscription}
                            onPaymentSuccess={handlePaymentSuccess}
                            onCancel={handleCancel}
                            userProfile={userProfile} // Pass user profile to form
                            buildingDetails={userProfile?.currentBuilding || userProfile?.selectedBuildingInfo}
                            isLoggedIn={isLoggedIn} // Pass login status to form
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionPayment;